// Mobile-mechanic state for the React reader. Encapsulates the
// tap-driven selection + TOC modal state + visible-term computation +
// the two effects (delegated click on prose marks, persistent
// .is-selected class). Pulled out of ReadingPage so the page component
// stays focused on layout composition.
//
// The CSS gate (@media max-width: 768px) governs whether the UI is
// visible — the hook runs at every viewport and is harmless on desktop
// (the rail still owns the desktop reading experience).

import { useCallback, useEffect, useMemo, useState, type RefObject } from "react";
import type { DocJSON, PublishedGlossary, PublishedTerm } from "../data/types";

export interface UseMobileMechanicOpts {
  doc: DocJSON;
  glossary: PublishedGlossary;
  /** The scroll container — same ref ReadingPage feeds to the other hooks. */
  scrollRef: RefObject<HTMLDivElement | null>;
  /** Output of useActiveSection. */
  activeSectionId: string | null;
  /** Output of useActiveGlossaryTerms. `null` = observer hasn't fired yet. */
  activeTerms: ReadonlySet<string> | null;
}

export interface MobileMechanic {
  selectedTermId: string | null;
  selectedEntry: PublishedTerm | null;
  tocOpen: boolean;
  /** Section-filtered + viewport-filtered. Same answer the Rail computes. */
  visibleTerms: PublishedTerm[];
  /** Tap a chip: select, scroll mark to 1/3 from top of the scroll container. */
  onChipSelect: (t: PublishedTerm) => void;
  /** Close the peek term sheet. */
  closeTermSheet: () => void;
  /** Open the modal TOC sheet. Auto-dismisses the term sheet so they never stack. */
  openToc: () => void;
  /** Close the modal TOC sheet. */
  closeToc: () => void;
}

export function useMobileMechanic({
  doc, glossary, scrollRef, activeSectionId, activeTerms,
}: UseMobileMechanicOpts): MobileMechanic {
  const [selectedTermId, setSelectedTermId] = useState<string | null>(null);
  const [tocOpen, setTocOpen] = useState(false);

  const termsById = useMemo(() => {
    const m = new Map<string, PublishedTerm>();
    for (const t of glossary.terms) m.set(t.term_id, t);
    return m;
  }, [glossary]);
  const selectedEntry =
    selectedTermId ? termsById.get(selectedTermId) ?? null : null;

  const activeSection = useMemo(
    () => doc.sections.find((s) => s.id === activeSectionId) ?? null,
    [doc, activeSectionId],
  );
  // Same two-level filter the Rail uses (section in view first, then
  // viewport-visible blocks within). Computed independently so the
  // chip strip and rail can diverge later without coupling.
  const visibleTerms = useMemo(() => {
    return glossary.terms.filter((t) => {
      const isSeed = t.origin === "openapi" || t.origin === "user";
      const sectionMatch =
        isSeed ||
        !activeSectionId ||
        (t.source_block_id != null &&
          activeSection?.blocks.some((b) => b.id === t.source_block_id)) === true;
      if (!sectionMatch) return false;
      return activeTerms === null || activeTerms.has(t.term);
    });
  }, [glossary, activeSection, activeSectionId, activeTerms]);

  // Delegated click — only catches inline marks. Rail rows, built-from
  // chips, and the ChipStrip chips have their own handlers.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const handler = (e: MouseEvent) => {
      const target = (e.target as HTMLElement | null)?.closest<HTMLElement>(
        ".maisight-term-mark",
      );
      if (!target) return;
      const id = target.dataset.termId;
      if (!id) return;
      setSelectedTermId(id);
    };
    el.addEventListener("click", handler);
    return () => el.removeEventListener("click", handler);
  }, [scrollRef]);

  // Parallel to .is-lit, but persists across the tap-driven selection.
  // Targets every occurrence of the term so all visible instances light
  // together.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.querySelectorAll(".maisight-term-mark.is-selected").forEach((m) =>
      m.classList.remove("is-selected"),
    );
    if (!selectedTermId) return;
    el.querySelectorAll<HTMLElement>(
      `.maisight-term-mark[data-term-id="${CSS.escape(selectedTermId)}"]`,
    ).forEach((m) => m.classList.add("is-selected"));
  }, [selectedTermId, scrollRef]);

  const onChipSelect = useCallback(
    (t: PublishedTerm) => {
      setSelectedTermId(t.term_id);
      const el = scrollRef.current;
      if (!el) return;
      const mark = el.querySelector<HTMLElement>(
        `.maisight-term-mark[data-term-id="${CSS.escape(t.term_id)}"]`,
      );
      if (!mark) return;
      const scrollRect = el.getBoundingClientRect();
      const markRect = mark.getBoundingClientRect();
      const target =
        el.scrollTop + (markRect.top - scrollRect.top) - scrollRect.height / 3;
      el.scrollTo({ top: target, behavior: "smooth" });
    },
    [scrollRef],
  );

  const closeTermSheet = useCallback(() => setSelectedTermId(null), []);
  const openToc = useCallback(() => {
    setSelectedTermId(null);
    setTocOpen(true);
  }, []);
  const closeToc = useCallback(() => setTocOpen(false), []);

  return {
    selectedTermId, selectedEntry, tocOpen, visibleTerms,
    onChipSelect, closeTermSheet, openToc, closeToc,
  };
}
